import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  GraduationCap,
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  Zap,
  ArrowRight,
  Gem,
  Gift,
  Palette,
  Camera,
  Crown,
  BookOpen,
  Trophy,
  Plane,
  Globe,
  PartyPopper,
  User
} from 'lucide-react';
import { toast } from 'sonner';

// Product categories
const PRODUCT_CATEGORIES = [
  { value: 'bomboniere', label: 'Bomboniere', icon: Gift },
  { value: 'inviti', label: 'Inviti e partecipazioni', icon: MessageSquare },
  { value: 'confettata', label: 'Confettata / Sweet table', icon: Gift },
  { value: 'decorazioni', label: 'Decorazioni e allestimenti', icon: Sparkles },
  { value: 'centrotavola', label: 'Centrotavola', icon: Star },
  { value: 'torta', label: 'Torta laurea personalizzata', icon: GraduationCap },
  { value: 'photo_booth', label: 'Photo booth props', icon: Camera },
  { value: 'corona', label: 'Corona d\'alloro', icon: Crown },
  { value: 'tableau', label: 'Tableau e cartellonistica', icon: Palette },
  { value: 'gadget', label: 'Gadget personalizzati', icon: PartyPopper },
  { value: 'regali', label: 'Regali personalizzati', icon: Gift },
  { value: 'pacchetto', label: 'Pacchetto completo', icon: Crown },
  { value: 'altro', label: 'Altro', icon: Sparkles },
];

// Degree types
const DEGREE_TYPES = [
  { value: 'triennale', label: 'Laurea Triennale' },
  { value: 'magistrale', label: 'Laurea Magistrale' },
  { value: 'ciclo_unico', label: 'Laurea Ciclo Unico' },
  { value: 'dottorato', label: 'Dottorato' },
  { value: 'master', label: 'Master' },
  { value: 'specializzazione', label: 'Specializzazione' },
  { value: 'diploma', label: 'Diploma' },
  { value: 'altro', label: 'Altro' },
];

// Themes
const THEMES = [
  { value: 'classico', label: 'Classico / Elegante', icon: Crown },
  { value: 'viaggio', label: 'Viaggio / Avventura', icon: Plane },
  { value: 'libri', label: 'Libri / Letteratura', icon: BookOpen },
  { value: 'scientifico', label: 'Scientifico / Tecnologia', icon: Zap },
  { value: 'artistico', label: 'Artistico / Creativo', icon: Palette },
  { value: 'natura', label: 'Natura / Botanico', icon: Heart },
  { value: 'vintage', label: 'Vintage / Retrò', icon: Star },
  { value: 'moderno', label: 'Moderno / Minimal', icon: Gem },
  { value: 'divertente', label: 'Divertente / Goliardico', icon: PartyPopper },
  { value: 'personalizzato', label: 'Tema personalizzato', icon: Sparkles },
];

// Colors
const COLOR_SCHEMES = [
  { value: 'rosso_oro', label: 'Rosso e oro (classico)', icon: Crown },
  { value: 'blu_oro', label: 'Blu e oro', icon: Star },
  { value: 'verde_oro', label: 'Verde e oro', icon: Heart },
  { value: 'nero_oro', label: 'Nero e oro', icon: Gem },
  { value: 'bordeaux', label: 'Bordeaux e avorio', icon: Crown },
  { value: 'colori_facolta', label: 'Colori della facoltà', icon: GraduationCap },
  { value: 'pastello', label: 'Colori pastello', icon: Sparkles },
  { value: 'personalizzato', label: 'Colori personalizzati', icon: Palette },
];

// Guest counts
const GUEST_COUNTS = [
  { value: '10-30', label: '10-30 invitati' },
  { value: '30-60', label: '30-60 invitati' },
  { value: '60-100', label: '60-100 invitati' },
  { value: '100-150', label: '100-150 invitati' },
  { value: '150+', label: '150+ invitati' },
];

// Budget
const BUDGET_OPTIONS = [
  { value: '100-300', label: '€100 - €300', description: 'Bomboniere/gadget' },
  { value: '300-600', label: '€300 - €600', description: 'Allestimento base' },
  { value: '600-1000', label: '€600 - €1000', description: 'Festa curata' },
  { value: '1000-1500', label: '€1000 - €1500', description: 'Evento elaborato' },
  { value: '1500-2500', label: '€1500 - €2500', description: 'Premium' },
  { value: '2500+', label: '€2500+', description: 'Esclusivo' },
];

// Timeline
const TIMELINE_OPTIONS = [
  { value: '2weeks', label: '2 Settimane', description: 'Urgente' },
  { value: '1month', label: '1 Mese', description: 'Prioritario' },
  { value: '2months', label: '2 Mesi', description: 'Standard' },
  { value: '3months', label: '3+ Mesi', description: 'Con calma' },
  { value: 'flexible', label: 'Flessibile', description: 'Data non definita' },
];

// Examples
const GRADUATION_EXAMPLES = [
  {
    idea: "Bomboniere laurea in medicina personalizzate",
    result: "Mini stetoscopi in metallo con incisione nome e data, confezionati in scatoline con confetti rossi",
    image: "https://images.unsplash.com/photo-1627556704302-624286467c65?w=400&h=300&fit=crop",
    category: "Bomboniere"
  },
  {
    idea: "Inviti laurea tema viaggio per ingegneria",
    result: "Partecipazioni a forma di biglietto aereo con boarding pass personalizzato, busta kraft con timbri",
    image: "https://images.pexels.com/photos/11503482/pexels-photo-11503482.jpeg?w=400&h=300&fit=crop",
    category: "Inviti"
  },
  {
    idea: "Allestimento completo per festa di laurea",
    result: "Decorazioni tema libri, tableau con mappa del mondo, photo booth accademico, confettata coordinata",
    image: "https://images.pexels.com/photos/8106557/pexels-photo-8106557.jpeg?w=400&h=300&fit=crop",
    category: "Allestimento"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "Per la mia laurea in architettura volevo qualcosa di speciale. L'artigiano ha creato bomboniere a forma di piccole case in ceramica, ognuna diversa. Un successo!",
    author: "Giulia P.",
    location: "Milano"
  },
  {
    text: "Gli inviti per la festa erano fantastici: biglietti d'aereo personalizzati con tutti i dettagli del mio percorso. Gli invitati li hanno conservati come ricordo!",
    author: "Marco R.",
    location: "Roma"
  },
  {
    text: "Festa di laurea indimenticabile! Dalla corona d'alloro fatta a mano al tableau con le foto del mio percorso universitario, tutto era perfetto.",
    author: "Alessia T.",
    location: "Bologna"
  }
];

// FAQ
const FAQ_ITEMS = [
  {
    question: "Che bomboniere vanno per una laurea?",
    answer: "Le più richieste sono: oggetti legati alla facoltà (stetoscopi per medicina, bilancine per giurisprudenza), mini libri, portachiavi personalizzati, candele profumate, piantine. L'artigiano può creare qualcosa di unico legato al tuo percorso."
  },
  {
    question: "Posso personalizzare con il colore della mia facoltà?",
    answer: "Assolutamente! Ogni facoltà ha il suo colore tradizionale (rosso medicina, blu ingegneria, verde agraria, etc.). Gli artigiani possono coordinare tutto con i colori della tua facoltà."
  },
  {
    question: "Fate anche corone d'alloro artigianali?",
    answer: "Sì! Abbiamo artigiani specializzati in corone d'alloro in tessuto, feltro o materiali naturali conservati. Molto più belle e durature di quelle standard."
  },
  {
    question: "Con quanto anticipo devo ordinare?",
    answer: "Per bomboniere e piccoli gadget bastano 2-3 settimane. Per allestimenti completi o corone personalizzate, meglio 4-6 settimane. Se hai poco tempo, alcuni artigiani offrono servizi express."
  }
];

export default function LaureeRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    graduate_name: '',
    degree_type: '',
    faculty: '',
    product_category: '',
    theme: '',
    color_scheme: '',
    guest_count: '',
    budget: '',
    timeline: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    if (formData.attachments.length + files.length > 5) { toast.error('Max 5 immagini'); return; }
    setUploadingFile(true);
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 5 * 1024 * 1024) continue;
      try {
        const fileName = `graduation-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const { error } = await supabase.storage.from('rarifex-uploads').upload(`requests/${fileName}`, file);
        if (error) throw error;
        const { data: { publicUrl } } = supabase.storage.from('rarifex-uploads').getPublicUrl(`requests/${fileName}`);
        setFormData(prev => ({ ...prev, attachments: [...prev.attachments, publicUrl] }));
      } catch (error) { toast.error('Errore upload'); }
    }
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({ ...prev, attachments: prev.attachments.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description.trim()) { toast.error('Descrivi cosa cerchi'); return; }
    if (!user) {
      setShowAuthPrompt(true);
      localStorage.setItem('rarifex_pending_graduation_request', JSON.stringify(formData));
      return;
    }
    setLoading(true);
    try {
      const degreeLabel = DEGREE_TYPES.find(t => t.value === formData.degree_type)?.label || '';
      const productLabel = PRODUCT_CATEGORIES.find(t => t.value === formData.product_category)?.label || '';
      const themeLabel = THEMES.find(t => t.value === formData.theme)?.label || '';
      const colorLabel = COLOR_SCHEMES.find(t => t.value === formData.color_scheme)?.label || '';
      const guestLabel = GUEST_COUNTS.find(t => t.value === formData.guest_count)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      let fullDescription = formData.description;
      const details = [];
      if (formData.graduate_name) details.push(`🎓 Laureando/a: ${formData.graduate_name}`);
      if (degreeLabel) details.push(`📜 Tipo: ${degreeLabel}`);
      if (formData.faculty) details.push(`🏛️ Facoltà: ${formData.faculty}`);
      if (productLabel) details.push(`🎁 Cerco: ${productLabel}`);
      if (themeLabel) details.push(`✨ Tema: ${themeLabel}`);
      if (colorLabel) details.push(`🎨 Colori: ${colorLabel}`);
      if (guestLabel) details.push(`👥 Invitati: ${guestLabel}`);
      if (budgetLabel) details.push(`💰 Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️ Tempi: ${timelineLabel}`);
      
      if (details.length > 0) {
        fullDescription += `\n\n━━━━━━━━━━━━━━━━━━━━━━━\n🎓 DETTAGLI LAUREA\n━━━━━━━━━━━━━━━━━━━━━━━\n` + details.join('\n');
      }
      
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: user.id,
          title: formData.title || `Laurea ${formData.graduate_name || ''} - ${productLabel}`.trim(),
          description: fullDescription,
          category: 'Lauree',
          price_range: formData.budget || 'flexible',
          timeline: formData.timeline || 'flexible',
          attachments: formData.attachments,
        }),
      });
      if (!response.ok) throw new Error('Errore');
      toast.success('Richiesta inviata!');
      navigate('/requests');
    } catch (error) { toast.error('Errore nell\'invio'); }
    finally { setLoading(false); }
  };

  const scrollToForm = () => document.getElementById('graduation-form')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <>
      <Helmet>
        <title>Laurea Su Misura | Bomboniere e Inviti Artigianali | Rarifex</title>
        <meta name="description" content="Organizza la festa di laurea perfetta con bomboniere e inviti artigianali italiani. Gadget personalizzati, allestimenti a tema, corone d'alloro." />
        <meta name="keywords" content="laurea, bomboniere laurea, inviti laurea, festa laurea, corona alloro, gadget laurea, confettata laurea" />
        <link rel="canonical" href="https://www.rarifex.com/lauree-su-misura" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-red-50/50 via-white to-orange-50/30">
        
        {/* HERO */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-red-100/50 via-transparent to-transparent" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-red-100 text-red-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <GraduationCap className="w-4 h-4" />
                    Festa di Laurea
                  </div>
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    La laurea merita{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
                      una festa unica
                    </span>
                  </h1>
                  <p className="text-xl text-slate-600 mb-8">
                    Bomboniere, inviti e allestimenti artigianali per celebrare il tuo traguardo.
                  </p>
                  <p className="text-lg text-slate-500 mb-8">
                    <strong className="text-slate-700">Gratuito e senza impegno.</strong>
                  </p>
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-500" /><span>Artigiani verificati</span></div>
                    <div className="flex items-center gap-2"><Clock className="w-5 h-5 text-red-500" /><span>Risposte in 24-48h</span></div>
                    <div className="flex items-center gap-2"><Shield className="w-5 h-5 text-slate-500" /><span>Made in Italy</span></div>
                  </div>
                </div>
                
                <div id="graduation-form">
                  <Card className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Nome laureando/a</Label>
                          <Input placeholder="Es: Marco" value={formData.graduate_name} onChange={(e) => handleInputChange('graduate_name', e.target.value)} className="mt-1.5" data-testid="graduation-name" />
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Tipo laurea</Label>
                          <select value={formData.degree_type} onChange={(e) => handleInputChange('degree_type', e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="graduation-degree">
                            <option value="">Seleziona...</option>
                            {DEGREE_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                          </select>
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Facoltà / Corso</Label>
                        <Input placeholder="Es: Medicina, Ingegneria, Giurisprudenza..." value={formData.faculty} onChange={(e) => handleInputChange('faculty', e.target.value)} className="mt-1.5" data-testid="graduation-faculty" />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Descrivi cosa cerchi *</Label>
                        <Textarea placeholder="Racconta la tua idea per la festa di laurea... Stile, atmosfera, dettagli speciali." value={formData.description} onChange={(e) => handleInputChange('description', e.target.value)} className="mt-1.5 min-h-[100px]" required data-testid="graduation-description" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Cosa cerchi?</Label>
                          <select value={formData.product_category} onChange={(e) => handleInputChange('product_category', e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="graduation-product">
                            <option value="">Seleziona...</option>
                            {PRODUCT_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Tema</Label>
                          <select value={formData.theme} onChange={(e) => handleInputChange('theme', e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="graduation-theme">
                            <option value="">Seleziona...</option>
                            {THEMES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Colori</Label>
                          <select value={formData.color_scheme} onChange={(e) => handleInputChange('color_scheme', e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="graduation-colors">
                            <option value="">Seleziona...</option>
                            {COLOR_SCHEMES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Invitati</Label>
                          <select value={formData.guest_count} onChange={(e) => handleInputChange('guest_count', e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="graduation-guests">
                            <option value="">Seleziona...</option>
                            {GUEST_COUNTS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Budget</Label>
                          <select value={formData.budget} onChange={(e) => handleInputChange('budget', e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="graduation-budget">
                            <option value="">Seleziona...</option>
                            {BUDGET_OPTIONS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Quando?</Label>
                          <select value={formData.timeline} onChange={(e) => handleInputChange('timeline', e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="graduation-timeline">
                            <option value="">Seleziona...</option>
                            {TIMELINE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Foto ispirazione</Label>
                        <div className="mt-1.5">
                          {formData.attachments.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {formData.attachments.map((url, idx) => (
                                <div key={idx} className="relative group">
                                  <img src={url} alt="" className="w-14 h-14 object-cover rounded-lg" />
                                  <button type="button" onClick={() => removeAttachment(idx)} className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100"><X className="w-3 h-3" /></button>
                                </div>
                              ))}
                            </div>
                          )}
                          {formData.attachments.length < 5 && (
                            <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-red-300">
                              <input type="file" accept="image/*" multiple onChange={handleFileUpload} className="hidden" disabled={uploadingFile} />
                              {uploadingFile ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5 text-slate-400" />}
                              <span className="text-sm text-slate-500">Carica ({formData.attachments.length}/5)</span>
                            </label>
                          )}
                        </div>
                      </div>
                      <Button type="submit" disabled={loading} className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-red-500 to-orange-600 hover:from-red-600 hover:to-orange-700" data-testid="graduation-submit">
                        {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <GraduationCap className="w-5 h-5 mr-2" />}
                        Ricevi proposte gratis
                      </Button>
                    </form>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ESEMPI */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Ecco cosa creano i nostri artigiani</h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {GRADUATION_EXAMPLES.map((ex, idx) => (
                <Card key={idx} className="overflow-hidden hover:shadow-xl transition-shadow">
                  <img src={ex.image} alt={ex.result} className="w-full h-48 object-cover" />
                  <div className="p-5">
                    <span className="text-xs font-medium text-red-600 uppercase">{ex.category}</span>
                    <p className="text-sm text-slate-500 mt-2">"{ex.idea}"</p>
                    <p className="font-semibold text-slate-800 mt-1">→ {ex.result}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="py-16 bg-red-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Cosa dicono i laureati</h2>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {TESTIMONIALS.map((t, idx) => (
                <Card key={idx} className="p-6 bg-white">
                  <div className="flex gap-1 mb-4">{[1,2,3,4,5].map(i => <Star key={i} className="w-5 h-5 fill-red-500 text-red-500" />)}</div>
                  <p className="text-slate-600 italic mb-4">"{t.text}"</p>
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center font-semibold text-red-700">{t.author[0]}</div>
                    <div><p className="font-medium">{t.author}</p><p className="text-slate-500 text-xs">{t.location}</p></div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-3xl font-bold text-center mb-12">Domande frequenti</h2>
            <div className="space-y-4">
              {FAQ_ITEMS.map((faq, idx) => (
                <div key={idx} className="border rounded-xl overflow-hidden">
                  <button onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)} className="w-full flex items-center justify-between p-5 text-left hover:bg-red-50">
                    <span className="font-medium">{faq.question}</span>
                    <ChevronRight className={`w-5 h-5 transition-transform ${expandedFaq === idx ? 'rotate-90' : ''}`} />
                  </button>
                  {expandedFaq === idx && <div className="px-5 pb-5 text-slate-600">{faq.answer}</div>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-b from-red-100 to-red-50">
          <div className="container mx-auto px-4 text-center">
            <GraduationCap className="w-16 h-16 text-red-600 mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4">Pronto a festeggiare la tua laurea?</h2>
            <p className="text-xl text-slate-600 mb-8">Gli artigiani italiani aspettano la tua richiesta.</p>
            <Button onClick={scrollToForm} size="lg" className="py-6 px-10 text-lg bg-gradient-to-r from-red-500 to-orange-600" data-testid="graduation-cta-final">
              <GraduationCap className="w-5 h-5 mr-2" /> Ricevi proposte gratis
            </Button>
          </div>
        </section>
      </div>

      {showAuthPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Accedi per continuare</h3>
            <p className="text-slate-600 mb-6">Per inviare la richiesta devi accedere.</p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowAuthPrompt(false)} className="flex-1">Annulla</Button>
              <Button onClick={() => navigate('/login?redirect=/lauree-su-misura')} className="flex-1 bg-red-500">Accedi</Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
